import type { ActivationAlertsContractSocialMediaGroup } from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { CircularLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ModalShell } from "../../../common/modal/ModalShell";
import { useSelectedGroupItemStore } from "../../../common/modal/useSelectedGroupItemStore";
import { POST_MODAL_CONTENT_HEIGHT } from "../constants";

import { Header } from "./Header";
import { ImagePreviewSection } from "./ImagePreviewSection";
import { PostDetailsSection } from "./PostDetailsSection";
import { useLazySocialPosts } from "./useLazySocialPosts";

const ADDITIONAL_POSTS_TRIGGER_INDEX = 7;

type SocialPostModalInnerProps = {
    favicon: string;
    group: ActivationAlertsContractSocialMediaGroup;
};

export const SocialPostModalInner = ({ favicon, group }: SocialPostModalInnerProps) => {
    const selectedItemIndex = useSelectedGroupItemStore((store) => store.selectedItemIndex);
    const setSelectedItemIndex = useSelectedGroupItemStore((store) => store.setSelectedItemIndex);
    const reset = useSelectedGroupItemStore((store) => store.reset);
    const { track } = useTracking();
    const hasMorePosts = group.postsCount > group.posts.length;
    const domain = group.domain;
    const groupPostsCount = group.postsCount;

    const shouldTriggerFetch =
        selectedItemIndex != null &&
        selectedItemIndex >= ADDITIONAL_POSTS_TRIGGER_INDEX &&
        selectedItemIndex < groupPostsCount;

    const { posts } = useLazySocialPosts({
        domain,
        socialMediaChannel: group.socialMediaChannel,
        initialPosts: group.posts,
        enabled: hasMorePosts,
        shouldTriggerFetch,
    });

    const currentPost = posts?.[selectedItemIndex ?? 0];

    if (selectedItemIndex == null || selectedItemIndex >= groupPostsCount) {
        return null;
    }

    const postCounter = `${selectedItemIndex + 1}/${groupPostsCount}`;

    const handleClose = () => {
        track("Alert Card", "click", "close alert/alerts/social-posts");
        reset();
    };

    return (
        <ModalShell
            selectedIndex={selectedItemIndex}
            itemCount={groupPostsCount}
            onIndexChange={setSelectedItemIndex}
            onClose={handleClose}
            dataAutomation="view-more-modal"
            header={
                <Header
                    domain={domain}
                    favicon={favicon}
                    platform={group.socialMediaChannel}
                    postCounter={postCounter}
                    onClose={handleClose}
                />
            }
        >
            <Flex
                gap="48px"
                padding="24px 16px 8px"
            >
                {currentPost ? (
                    <>
                        <ImagePreviewSection assetUrl={currentPost.assetUrl ?? undefined} />
                        <PostDetailsSection post={currentPost} />
                    </>
                ) : (
                    <Flex
                        height={POST_MODAL_CONTENT_HEIGHT}
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <CircularLoader isLarge />
                    </Flex>
                )}
            </Flex>
        </ModalShell>
    );
};
